import config from 'config';

import { YoutubeBroadcast, YoutubeStream } from 'types';
import { convertDateToTimestamp } from 'utils';
import { api, LoggerService } from 'services';

export type CreateBroadcastData = {
  title: string;
  description?: string;
};

export type CreateStreamData = {
  title: string;
  description?: string;
};

export type BindStreamToBroadcastData = {
  id: string;
  streamId: string;
};

const requests = {
  init: () =>
    new Promise<void>((resolve, reject) => {
      gapi.load('client', async () => {
        try {
          await gapi.client.init({ clientId: config.oauth.google.clientId });

          resolve();
        } catch (error) {
          LoggerService.log('LOAD_GAPI_SDK', error);

          if (error instanceof Error) {
            reject(error.message);
          }

          reject(error);
        }
      });
    }),
  getToken: () =>
    new Promise<{ accessToken: string; expiresAt: number }>((resolve, reject) => {
      gapi.auth.authorize(
        {
          client_id: config.oauth.google.clientId,
          response_type: 'token',
          scope: 'https://www.googleapis.com/auth/youtube'
        },
        token => {
          if (token.error) {
            reject(token.error);
          } else {
            const accessToken = token.access_token;
            const expiresAt = new Date();

            expiresAt.setSeconds(expiresAt.getSeconds() + parseInt(token.expires_in, 10));

            const expiresAtTS = convertDateToTimestamp(expiresAt);

            resolve({
              accessToken,
              expiresAt: expiresAtTS
            });
          }
        }
      );
    }),
  refreshToken: () =>
    new Promise<{ accessToken: string; expiresAt: number }>((resolve, reject) => {
      gapi.auth.authorize(
        {
          client_id: config.oauth.google.clientId,
          response_type: 'token',
          scope: 'https://www.googleapis.com/auth/youtube',
          immediate: true
        },
        token => {
          if (token.error) {
            reject(token.error);
          } else {
            const accessToken = token.access_token;
            const expiresAt = new Date();

            expiresAt.setSeconds(expiresAt.getSeconds() + parseInt(token.expires_in, 10));

            const expiresAtTS = convertDateToTimestamp(expiresAt);

            resolve({
              accessToken,
              expiresAt: expiresAtTS
            });
          }
        }
      );
    }),
  createBroadcast: ({ title, description }: CreateBroadcastData) =>
    api.instances.youtube.post<YoutubeBroadcast>(
      '/liveBroadcasts',
      {
        kind: 'youtube#liveBroadcast',
        snippet: {
          scheduledStartTime: new Date().toISOString(),
          title,
          description
        },
        contentDetails: {
          enableAutoStart: true,
          enableAutoStop: true,
          enableLowLatency: true
        },
        status: {
          privacyStatus: 'private'
        }
      },
      {
        params: {
          part: ['id', 'snippet', 'contentDetails', 'status'].join(',')
        }
      }
    ),
  createStream: ({ title, description }: CreateStreamData) =>
    api.instances.youtube.post<YoutubeStream>(
      '/liveStreams',
      {
        kind: 'youtube#liveStream',
        cdn: {
          format: '',
          ingestionType: 'rtmp',
          frameRate: 'variable',
          resolution: 'variable'
        },
        snippet: {
          title,
          description
        }
      },
      {
        params: {
          part: ['id', 'snippet', 'cdn', 'contentDetails', 'status'].join(',')
        }
      }
    ),
  bindBroadcastToStream: ({ id, streamId }: BindStreamToBroadcastData) =>
    api.instances.youtube.post<YoutubeBroadcast>('/liveBroadcasts/bind', undefined, {
      params: {
        part: ['id', 'snippet', 'contentDetails', 'status'].join(','),
        id,
        streamId
      }
    })
};

export default requests;
